import { useCallback, useMemo } from 'react';
import useSWR from 'swr';

import { Enum, EnumListKey, useAllEnums } from '~/data/enums';
import {
  BillableHoursProposalData,
  BillableHoursProposalProps,
} from '~/data/proposals';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import {
  EditCustomField,
  jsEditCustomFieldData,
} from '~/legacy-components/CustomDataFields';
import { BillableHoursTypeConfigSelectorProps } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/BillableHoursProposalForm/components/BillableHoursTypeConfigSelector';
import {
  BillableHoursProposalFormData,
  getCustomBillableHoursFieldAttributes,
  mapDefaultData,
  mapFormDataToLegacyPayload,
  submitBillableHoursProposal,
  useValidationSchema,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/BillableHoursProposalForm/data';
import { useBillableHoursTypeConfig } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/BillableHoursProposalForm/useBillableHoursTypeConfig';
import {
  CommonProposalFieldProps,
  EmployeeProposalDecision,
  EmployeeProposalUpdateCallback,
  UseCommonProposalFormOutput,
  transformProposalRequestProps,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { useCommonFieldProps } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/useCommonFieldProps';
import { useInitiateFormData } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/useInitiateFormData';
import { useProposalPermissions } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/useProposalPermissions';
import { useEmployeeDataForProposal } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/useEmployeeDataForProposal';
import { BillableHoursProposalFormProps } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposals/components/EmployeeBillableHoursProposalsLayout/useBillableHoursProposalsLayout';

import { useEmploymentEnumsFilter } from '../Common/useEmploymentEnumsFilter';
import { useScheduleEmployments } from '../Common/useScheduleEmployments';

type PropsType = {
  proposal: BillableHoursProposalProps;
  employeeId: number;
  onProposalUpdate: EmployeeProposalUpdateCallback;
  formProps: BillableHoursProposalFormProps;
};

type UseBillableHoursProposalFormOutput =
  UseCommonProposalFormOutput<BillableHoursProposalFormData> & {
    handleCalculateTotal: () => void;
    enums: {
      billableTypes: Enum[];
      billableProjects: Enum[];
    };
    customFields: EditCustomField[];
    fieldProps: CommonProposalFieldProps & {
      calculateTotal: {
        disabled: boolean;
      };
    };
    billableHoursTypeConfigSelector: BillableHoursTypeConfigSelectorProps;
    currency: string;
  };
//WILL COME FROM THE BE AFTER THE GET API IS FIXED
const customBillableHoursFieldsRes = {};

export const useBillableHoursProposalForm = (
  props: PropsType,
): UseBillableHoursProposalFormOutput => {
  const { proposal, employeeId, onProposalUpdate, formProps } = props;
  const { proposalData } = proposal;
  const { legalEntityCountryForUser, billableHoursTypeConfigs } = formProps;

  const { currency } = useEmployeeDataForProposal({ proposal });

  const { filterActiveEnums } = useEmploymentEnumsFilter();

  // Enums
  const { getEnums, allEnums } = useAllEnums();
  const enums = useMemo(() => {
    const getEnumOptions = (
      listKey: EnumListKey,
      selectedValue?: string,
    ): Enum[] => filterActiveEnums(getEnums(listKey), selectedValue);

    return {
      billableTypes: getEnumOptions(
        EnumListKey.REW_BILLABLE_TYPE,
        proposalData?.billableHoursTypeFK,
      ),
      billableProjects: getEnumOptions(
        EnumListKey.REW_BILLABLE_PROJECT,
        proposalData?.billableHoursProjectTypeFK,
      ),
      billableCustomData: getEnums(
        EnumListKey.CUSTOM_ENTITY_BILLABLE_HOURS_PROPOSAL,
      ),
    };
  }, [
    proposalData?.billableHoursTypeFK,
    proposalData?.billableHoursProjectTypeFK,
    getEnums,
    filterActiveEnums,
  ]);

  // Hours Type Config
  const {
    defaultBillableType,
    billableHoursTypeConfigSelector,
    selectedHoursTypeConfig,
  } = useBillableHoursTypeConfig({
    proposal,
    billableHoursTypeConfigs,
    billableTypes: enums.billableTypes,
    currency,
  });

  const {
    readOnlyMode,
    userRoles,
    loggedInEmployeeId,
    canSubmitForm,
    responsibleHRSelectEnabled,
  } = useProposalPermissions({ proposal });

  const { hasUpcomingEmployments } = useScheduleEmployments();
  const resolver = useYupValidationResolver(
    useValidationSchema(hasUpcomingEmployments),
    'react-hook-form',
  );

  const dataMapper = useCallback(
    (
      targetEmploymentId: number,
      proposalData?: BillableHoursProposalData,
      currentResponsibleHRId?: number,
    ) => {
      return mapDefaultData(
        targetEmploymentId,
        proposalData,
        currentResponsibleHRId,
        defaultBillableType,
      );
    },
    [defaultBillableType],
  );

  const {
    allResponsibleHRIds,
    isLoading,
    form,
    handleFormReset,
    handleSubmitRequest,
  } = useInitiateFormData<
    BillableHoursProposalProps,
    BillableHoursProposalFormData
  >({
    proposal,
    employeeId,
    dataMapper,
    resolver,
  });

  const {
    data: customBillableHoursFieldAttributesRes,
    isLoading: fieldAttributesLoading,
  } = useSWR('customEmployeeFieldsFieldAttributes', () =>
    getCustomBillableHoursFieldAttributes(),
  );

  const customDataLoading = fieldAttributesLoading;

  const customFields = useMemo(() => {
    if (!customDataLoading) {
      const hashedFields = Object.assign(
        {},
        ...customBillableHoursFieldAttributesRes!.map((field) => ({
          [field.fCustomDataAttributeName]: field,
        })),
      );

      return jsEditCustomFieldData({
        strEntity: 'BILLABLE_HOURS_PROPOSAL',
        customFieldAttributesHash: hashedFields,
        customFields: customBillableHoursFieldsRes!,
        legalEntityCountry: legalEntityCountryForUser,
        customNamePrefix: 'fBillableHoursProposalCustomFields.',
        allEnums,
      });
    }
    return [];
  }, [
    allEnums,
    customBillableHoursFieldAttributesRes,
    customDataLoading,
    legalEntityCountryForUser,
  ]);

  const handleFormSubmit = useCallback(
    (decision?: EmployeeProposalDecision) => {
      return handleSubmitRequest({
        onSuccess: onProposalUpdate,
        handleSubmission: (data, requestProps) => {
          requestProps = transformProposalRequestProps(
            requestProps,
            +data.employmentId,
          );
          return submitBillableHoursProposal(
            mapFormDataToLegacyPayload({
              data,
              isHRUser: userRoles.isHR,
              loggedInEmployeeId,
              decision,
              requestProps,
            }),
            requestProps,
          );
        },
      });
    },
    [handleSubmitRequest, onProposalUpdate, loggedInEmployeeId, userRoles],
  );

  const { watch, getValues, setValue } = form;

  const handleCalculateTotal = useCallback(() => {
    const ht = {
      state: selectedHoursTypeConfig?.fBillableHoursType,
      form: getValues('hoursType'),
    };
    const hoursTypeInSync = ht.state === ht.form;
    const hoursType = hoursTypeInSync ? ht.state : undefined;

    const hours = getValues('hours');

    if (hoursTypeInSync) {
      if (!!hoursType && !!hours) {
        const typeConfigAmount =
          selectedHoursTypeConfig?.fBillableHoursAmount || 0;

        setValue('total', hours * typeConfigAmount);
      } else {
        setValue('total', 0);
      }
    }
  }, [getValues, setValue, selectedHoursTypeConfig]);

  const watchValue = {
    hoursType: getValues('hoursType'),
    hours: watch('hours'),
    startDate: watch('paymentPeriodStartDate'),
    endDate: watch('paymentPeriodEndDate'),
  };

  const { isLoading: isLoadingCommonFieldProps, ...commonFieldProps } =
    useCommonFieldProps({
      startDate: { value: watchValue.startDate },
      endDate: { value: watchValue.endDate },
      employmentId: watch('employmentId'),
    });
  const fieldProps = {
    ...commonFieldProps,
    calculateTotal: {
      disabled:
        !watchValue.hours ||
        !watchValue.hoursType ||
        !selectedHoursTypeConfig?.fBillableHoursAmount,
    },
  };

  return {
    form,
    responsibleHRSelector: {
      options: allResponsibleHRIds,
      isResponsibleHR: userRoles.isResponsibleHR,
      enabled: responsibleHRSelectEnabled,
    },
    isLoading: {
      responsibleHR: isLoading,
      commonFieldProps: isLoadingCommonFieldProps,
    },
    billableHoursTypeConfigSelector,
    enums,
    handleFormSubmit,
    handleFormReset,
    handleCalculateTotal,
    customFields,
    readOnlyMode,
    canSubmitForm,
    fieldProps,
    currency,
  };
};
